import { useEffect, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import { api, ApiError, PublicPortalSite } from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

export default function AdminPortals() {
  const [portals, setPortals] = useState<PublicPortalSite[]>([]);
  const [workspaceNames, setWorkspaceNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | "draft" | "active">("all");
  const [visibilityFilter, setVisibilityFilter] = useState<"all" | "public" | "restricted">("all");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.publicPortal(),
      api.workspace().catch(() => ({ workspaces: [], members: [] }))
    ])
      .then(([p, w]) => {
        if (cancelled) return;
        setPortals(p.portals ?? []);
        const byId = new Map<string, string>();
        for (const ws of w.workspaces ?? []) byId.set(ws.id, ws.name);
        setWorkspaceNames(byId);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const rows = portals.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (visibilityFilter !== "all" && p.visibility !== visibilityFilter) return false;
    return true;
  });

  const t = useTableState(rows, {
    searchFields: ["id", "name", "slug", "workspaceId", "visibility", "authMode", "status"],
    initialPageSize: 25
  });

  const summary = {
    total: portals.length,
    active: portals.filter((p) => p.status === "active").length,
    draft: portals.filter((p) => p.status === "draft").length,
    public: portals.filter((p) => p.visibility === "public").length
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Public portals</h1>
          <p>Cross-tenant inventory of public portals. Visibility and audience are surfaced — portal contents stay scoped to their workspace.</p>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}
      {loading ? <SkeletonTable rows={5} columns={4} /> : (
        <>
          <section className="stat-grid">
            <div className="stat"><div className="stat-label">Total</div><div className="stat-value">{summary.total}</div></div>
            <div className="stat"><div className="stat-label">Active</div><div className="stat-value">{summary.active}</div></div>
            <div className="stat"><div className="stat-label">Draft</div><div className="stat-value">{summary.draft}</div></div>
            <div className="stat"><div className="stat-label">Public</div><div className="stat-value">{summary.public}</div></div>
          </section>

          <div className="card" style={{ margin: "16px 0" }}>
            <div className="card-body" style={{ padding: 12, display: "flex", gap: 8 }}>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
              </select>
              <select value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value as typeof visibilityFilter)}>
                <option value="all">All visibility</option>
                <option value="public">Public</option>
                <option value="restricted">Restricted</option>
              </select>
            </div>
          </div>

          {rows.length === 0 ? (
            <EmptyState
              icon="🌐"
              title="No public portals match the current filter"
              description="Workspace owners create portals from the Public portals page in their workspace. Portals are publishable shells around datasets and indicators."
            />
          ) : (
            <div className="table-scroll">
              <TableToolbar
            hideExport
                search={t.search}
                setSearch={t.setSearch}
                page={t.page}
                setPage={t.setPage}
                pageSize={t.pageSize}
                setPageSize={t.setPageSize}
                totalPages={t.totalPages}
                totalRows={t.totalRows}
                searchPlaceholder="Search portals…"
              />
              <table className="data-table">
                <thead><tr><th>Workspace</th><th>Name</th><th>Slug</th><th>Visibility</th><th>Auth</th><th>Status</th><th>Datasets</th><th>Updated</th><th>Actions</th></tr></thead>
                <tbody>
                  {t.paged.map((p) => (
                    <tr key={p.id}>
                      <td>{workspaceNames.get(p.workspaceId) ?? <code>{p.workspaceId}</code>}</td>
                      <td>{p.name}</td>
                      <td className="soft"><code>{p.slug}</code></td>
                      <td><span className={`pill ${p.visibility === "public" ? "pill-info" : "pill-muted"}`}>{p.visibility}</span></td>
                      <td className="soft">{p.authMode}</td>
                      <td><span className={`pill ${p.status === "active" ? "pill-success" : "pill-muted"}`}>{p.status}</span></td>
                      <td className="soft">{p.datasetIds.length}</td>
                      <td className="soft">{new Date(p.updatedAt).toLocaleString()}</td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          {p.visibility === "public" && (
                            <a className="btn btn-sm" href={`/p/${encodeURIComponent(p.slug)}`} target="_blank" rel="noreferrer">View</a>
                          )}
                          {p.status === "draft" ? (
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={async () => {
                                try {
                                  const { portal } = await api.setPublicPortalStatus(p.id, "publish");
                                  setPortals((prev) => prev.map((row) => (row.id === p.id ? portal : row)));
                                } catch (e) {
                                  setErr(e instanceof ApiError ? e.message : String(e));
                                }
                              }}
                            >
                              Publish
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={async () => {
                                try {
                                  const { portal } = await api.setPublicPortalStatus(p.id, "unpublish");
                                  setPortals((prev) => prev.map((row) => (row.id === p.id ? portal : row)));
                                } catch (e) {
                                  setErr(e instanceof ApiError ? e.message : String(e));
                                }
                              }}
                            >
                              Unpublish
                            </button>
                          )}
                          <Link className="btn btn-sm" to={`/admin/workspaces/${encodeURIComponent(p.workspaceId)}`}>Workspace</Link>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{ color: "#b91c1c" }}
                            onClick={async () => {
                              if (!window.confirm(`Delete portal "${p.name}" (${p.slug})? This is permanent.`)) return;
                              try {
                                await api.deletePublicPortal(p.id);
                                setPortals((prev) => prev.filter((row) => row.id !== p.id));
                              } catch (e) {
                                setErr(e instanceof ApiError ? e.message : String(e));
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
